
import { format } from "date-fns"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useEffect, useState } from "react"

interface SleepWindowsData {
  hour_label: string;
  sleep_start_count: number;
  sleep_end_count: number;
}

export interface SleepWindowsChartProps {
  data?: SleepWindowsData[];
  childId?: string;
  records?: any[];
}

export function SleepWindowsChart({ data, childId, records }: SleepWindowsChartProps) {
  const [chartData, setChartData] = useState<SleepWindowsData[]>([]);

  useEffect(() => {
    if (data) {
      setChartData(data);
      return;
    }
    
    if (records?.length) {
      // Process records to create chart data
      const hourCounts: Record<string, { starts: number, ends: number }> = {};
      
      // Initialize hours
      for (let i = 0; i < 24; i++) {
        const hour = i < 10 ? `0${i}:00` : `${i}:00`;
        hourCounts[hour] = { starts: 0, ends: 0 };
      }
      
      // Count sleep starts and ends by hour
      records.forEach(record => {
        const startTime = new Date(record.start_time);
        const endTime = new Date(record.end_time);
        
        const startHour = `${String(startTime.getHours()).padStart(2, '0')}:00`;
        const endHour = `${String(endTime.getHours()).padStart(2, '0')}:00`;
        
        hourCounts[startHour].starts += 1;
        hourCounts[endHour].ends += 1;
      });
      
      // Convert to chart data format
      const formattedData = Object.entries(hourCounts).map(([hour, counts]) => ({
        hour_label: hour,
        sleep_start_count: counts.starts,
        sleep_end_count: counts.ends
      }));
      
      setChartData(formattedData);
    } else {
      setChartData([]);
    }
  }, [data, records]);

  if (!chartData?.length) {
    return <div className="text-gray-400">No sleep data available</div>
  }

  return (
    <div className="w-full h-[300px]">
      <h3 className="text-sm font-medium mb-4">Sleep Windows</h3>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis 
            dataKey="hour_label" 
            className="text-xs"
            interval={2}
            tick={{ fill: 'currentColor' }}
          />
          <YAxis className="text-xs" tick={{ fill: 'currentColor' }} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Legend />
          <Bar 
            dataKey="sleep_start_count" 
            name="Sleep Start" 
            fill="#4f46e5" 
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="sleep_end_count" 
            name="Sleep End" 
            fill="#ec4899" 
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
